/**
 * ImageMessage
 *
 * Renders an image message with thumbnail that opens a lightbox on click.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function ImageMessage({ media }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const previewRef = useRef(null);
  useEffect(() => {
    const dialog = previewRef.current;
    if (!lightboxOpen || !dialog) return undefined;
    dialog.showModal();
    return () => dialog.close();
  }, [lightboxOpen]);

  if (!media?.url) return null;

  // Container gets explicit dimensions from media so placeholder matches loaded image
  const displayWidth = Math.min(media.width || 250, 250);
  const displayHeight = media.width && media.height
    ? Math.round(displayWidth * (media.height / media.width))
    : Math.round(displayWidth * 0.75);

  return (
    <>
      {/* Thumbnail */}
      <button
        type="button"
        aria-label={`Open ${media.fileName || 'image'}`}
        className="cursor-pointer rounded-lg overflow-hidden"
        style={{ width: displayWidth, height: displayHeight }}
        onClick={() => setLightboxOpen(true)}
      >
        {!imageLoaded && (
          <div className="bg-base-300 animate-pulse rounded-lg w-full h-full" />
        )}
        <img
          src={media.thumbnailUrl || media.url}
          alt="Image"
          className={`rounded-lg w-full h-full ${imageLoaded ? '' : 'hidden'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />
      </button>

      {/* Lightbox */}
      {lightboxOpen && createPortal(
        <dialog
          ref={previewRef}
          aria-label={media.fileName || 'Image preview'}
          className="modal bg-black/80 p-4"
          onClose={() => setLightboxOpen(false)}
          onClick={event => { if (event.target === event.currentTarget) previewRef.current.close(); }}
        >
          <button
            type="button"
            aria-label="Close image preview"
            className="absolute top-4 right-4 btn btn-circle btn-ghost text-white"
            onClick={() => previewRef.current.close()}
          >
            ✕
          </button>
          <img
            src={media.url}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        </dialog>,
        document.body,
      )}
    </>
  );
}
